import { Button, Form, Grid, Header, Icon, Segment } from "semantic-ui-react";
import PaymentInformation from "./PaymentInformation";
import {
    DateTimeUtils,
    Modal,
    Notification,
    STATUS_TYPES,
    Step,
    USER_PERMISSIONS
} from "dyl-components";
import { Controller, useFormContext } from "react-hook-form";
import { useContext, useState } from "react";
import { QuoteBuilderContext } from "shared/context/QuoteBuilderProvider";
import PaymentMethod from "./PaymentMethod";

import "./index.scss";
import { useDispatch, useSelector } from "react-redux";
import invoiceActions from "actions/invoice";
import accountActions from "actions/account";
import paymentActions from "actions/payment";
import { useParams } from "react-router-dom";
import pipelineActions from "actions/pipeline";
import pbx_config from "actions/pbx_config";
import { DYL_ACCOUNT_ID } from "shared/modals/QuoteBuilderModal/helper";
import RestrictedView from "shared/RestrictedView";

const STEPS = [
    {
        icon: <Icon className="fas fa-box-dollar" size="large" />,
        title: "Order",
        completed: true,
    },
    {
        icon: <Icon className="fas fa-file-invoice-dollar" size="large" />,
        title: "Checkout",
        active: true,
    },
];

const Payment = ({ account_id, isEditOrder }) => {
    const {
        formState: { control, isValid },
        handleSubmit,
    } = useFormContext();
    const {
        onEditOrder,
        quoteBuilderConfig,
        onViewInvoice,
        onRefreshAccountInformation,
    } = useContext(QuoteBuilderContext);

    const [selectedPaymentMethod, setSelectedPaymentMethod] = useState(null);
    const isDyl = useSelector(state => state.auth.dyl_employee);

    const onChangeSelectedPaymentMethod = (method) => {
        setSelectedPaymentMethod(method);
    };

    const isViewingAccount = useParams()?.account_id;

    const dispatch = useDispatch();
    const order_id = quoteBuilderConfig?.id;
    const paymentMethods = useSelector(
        (state) => state.payment.profile?.paymentProfiles || []
    );
    const customer_profile_id = useSelector(
        (state) => state.payment.profile?.customerProfileId
    );

    const opportunity_id = useSelector(
        (state) => state.order.order?.opportunity_id
    );
    const account_fund_type = useSelector(
        (state) => state.order.order?.account?.fund_type
    );
    const customer_status = useSelector(
        (state) => state.order.order?.account?.customer_status
    );
    const isReactivating = customer_status === "canceled" || customer_status === "paused";
    const isDYLAccount = Number(account_id) === DYL_ACCOUNT_ID;
    const onProcess = async (data) => {
        const term = data.term;
        const isDemo = term === "demo";
        const otherInfo = (() => {
            if (isDemo) {
                return {
                    order_type: "demo",
                };
            }
            const paymentInfo = paymentMethods.find(
                (paymentMethod) =>
                    data.payment_method ===
                    paymentMethod.customerPaymentProfileId
            );
            const paymentDetails = (() => {
                if (selectedPaymentMethod === "custom-ach") {
                    return {
                        custom: data.custom_ach,
                    };
                }
                if (!isDyl || isDYLAccount) {
                    return {};
                }
                const { creditCard, bankAccount } = paymentInfo.payment;
                if (creditCard) {
                    const { cardType, issuerNumber, ...otherInfo } = creditCard;
                    return {
                        customerPaymentProfileId: data.payment_method,
                        paymentInfo: {
                            creditCard: otherInfo,
                        }
                    };
                }
                return {
                    customerPaymentProfileId: data.payment_method,
                    paymentInfo: {
                        bankAccount,
                    }
                };
            })();
            if (term?.includes("trial")) {
                const [termType, numberOfDays] = term.split("/");
                return {
                    ...paymentDetails,
                    order_type: termType,
                    trial_period: Number(numberOfDays)
                };
            }
            if (term?.includes("net")) {
                const [termType, numberOfDays] = term.split("/");
                return {
                    ...paymentDetails,
                    order_type: termType,
                    term: Number(numberOfDays)
                };
            }
            return paymentDetails;
        })();
        const buyer_notes = data.buyer_notes;
        const note = (() => {
            if (!term?.includes("trial")) {
                return buyer_notes;
            }
            const [, numberOfDays] = term.split("/");
            return `
                Trial end date: ${DateTimeUtils.changeFormat(
                    DateTimeUtils.getNext(
                        Number(numberOfDays),
                        "day",
                        false,
                        true
                    ),
                    DateTimeUtils.DATE_FORMAT,
                    DateTimeUtils.WORD_DATE_FORMAT
                )}
                ${buyer_notes || ""}
                `;
        })();
        const payload = {
            ...otherInfo,
            account_id,
            opportunity_id,
            name: data.invoice_name,
            note,
            ...(isDyl ? { customer_profile_id } : {})
        };
        try {
            if (isDyl && ((isDemo) || (account_fund_type === "demo" && isReactivating))) {
                await dispatch(
                    paymentActions.updateProcessor(account_id, { processor: isDemo ? "demo" : "authorize.net" })
                );
            }
            const { invoice_id, subscription_ids } = await dispatch(
                invoiceActions.create(payload, null, order_id)
            );
            if (isReactivating) {
                await dispatch(
                    accountActions.reactivateCustomer(account_id, {
                        fund_type: (() => {
                            const term = data.term;
                            if (term?.includes("trial")) {
                                return "trial";
                            }
                            if (term === "demo") {
                                return term;
                            }
                            return "funded";
                        })(),
                        pipeline_id: opportunity_id
                    })
                )
            } else if (opportunity_id) {
                await dispatch(pipelineActions.upsell(opportunity_id, { account_id }));
            }
            if (account_fund_type === "unfunded") {
                await dispatch(
                    accountActions.updateFundType(account_id, {
                        fund_type: (() => {
                            if (term?.includes("trial")) {
                                return "trial";
                            }
                            if (term === "demo") {
                                return term;
                            }
                            return "funded";
                        })(),
                    })
                );
            }
            const numberOfExtensionsToCreate = data.cart?.find(item => item?.plan_features?.includes("phone"))?.quantity || 0;
            if (isDyl && numberOfExtensionsToCreate > 0) {
                await dispatch(pbx_config.createExtension({ count: numberOfExtensionsToCreate, subscription_ids, account_id }));
            }
            Notification.alert(
                "Successfully processed order!",
                STATUS_TYPES.SUCCESS
            );
            if (isViewingAccount) {
                await dispatch(accountActions.readAccount(account_id));
            }
            onViewInvoice(invoice_id);
            onRefreshAccountInformation();
            
        } catch (e) {
            console.log(e);
            Notification.alert("Failed to process order", STATUS_TYPES.ERROR);
        }
    };

    const isProcessingPaymentDetails = useSelector(
        (state) =>
            state.payment.isUpdatingPaymentProcessor ||
            state.invoice.isCreating ||
            state.account.isUpdatingFundType ||
            state.customers.isCreatingCustomer ||
            state.account.customerBeingReactivated ||
            state.pipeline.isUpselling ||
            state.pbx_config.isCreatingExtensions
    );

    const { isReadingPaymentInfo } = useSelector((state) => ({
        isReadingPaymentInfo:
            state.payment.isReadingCustomerFormToken ||
            state.payment.isReadingCustomerProfile ||
            state.payment.isReadingPaymentProfile,
    }));
    return (
        <>
            <Modal.Content scrolling>
                <Form
                    size="small"
                    noValidate
                    loading={isProcessingPaymentDetails || isReadingPaymentInfo}
                >
                    <Segment size="tiny" basic>
                        <Grid>
                            <Grid.Row>
                                <Grid.Column>
                                    <Step.Group horizontal>
                                        {STEPS.map(({ icon, ...step }) => (
                                            <Step {...step} key={step.name}>
                                                {icon}
                                            </Step>
                                        ))}
                                    </Step.Group>
                                </Grid.Column>
                            </Grid.Row>
                            <Grid.Row>
                                <Grid.Column>
                                    <Header color="primary">
                                        Invoice Information
                                    </Header>
                                    <PaymentInformation
                                        onChangeSelectedPaymentMethod={
                                            onChangeSelectedPaymentMethod
                                        }
                                        isDYLAccount={isDYLAccount}
                                    />
                                    <Header color="primary">
                                        Payment Method
                                    </Header>
                                    <PaymentMethod
                                        onChangeSelectedPaymentMethod={
                                            onChangeSelectedPaymentMethod
                                        }
                                        selectedPaymentMethod={
                                            selectedPaymentMethod
                                        }
                                        account_id={account_id}
                                    />
                                    <Controller
                                        control={control}
                                        name="buyer_notes"
                                        render={({
                                            field: { name, value, onChange },
                                        }) => (
                                            <Form.TextArea
                                                name={name}
                                                value={value}
                                                onChange={(_, { value }) => {
                                                    onChange({
                                                        target: { name, value },
                                                    });
                                                }}
                                                label="Buyer Notes"
                                            />
                                        )}
                                    />
                                </Grid.Column>
                            </Grid.Row>
                        </Grid>
                    </Segment>
                </Form>
            </Modal.Content>
            <Modal.Actions>
                <Button
                    basic
                    onClick={() => {
                        onEditOrder(order_id, account_id);
                    }}
                    type="button"
                    color="primary"
                    disabled={isProcessingPaymentDetails}
                >
                    Back to Order
                </Button>
                <RestrictedView type="disabled" userPermission={isEditOrder ? USER_PERMISSIONS.ORDERS_EDIT : USER_PERMISSIONS.ORDERS_ADD} userRestrict>
                    <Button
                        disabled={
                            !isValid || isProcessingPaymentDetails
                        }
                        type="submit"
                        color="primary"
                        onClick={handleSubmit(onProcess)}
                        loading={isProcessingPaymentDetails}
                    >
                        {isDyl ? "Process" : "Generate Invoice"}
                    </Button>
                </RestrictedView>
            </Modal.Actions>
        </>
    );
};

export default Payment;
